"use client";

import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useFetch } from "@/lib/use-fetch";
import { Alert, Badge, Button, Card, Input, Modal, PageHeader, Textarea } from "@/components/ui";
import { SenderSelect } from "@/components/sender-select";
import { TemplatePicker } from "@/components/pickers";
import { useToast } from "@/components/toast";
import { ConfirmRow, MessagePreview, SenderHeader } from "@/components/confirm-sms";
import { estimateSegments } from "@/lib/segments";
import { buildRateLookup, categorize } from "@/lib/sms-rates";
import { notifyAccountChanged } from "@/lib/account";
import type { Account, SendResult, SmsRate } from "@/lib/types";

const CAT_TONE: Record<string, "green" | "blue" | "amber"> = { LOCAL: "green", NATIONAL: "blue", INTERNATIONAL: "amber" };
const CAT_LABEL: Record<string, string> = { LOCAL: "Local", NATIONAL: "National", INTERNATIONAL: "International" };

function OptionRow({
  checked,
  onChange,
  title,
  desc
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  desc: string;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 transition hover:bg-gray-50">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
      <span>
        <span className="block text-sm font-medium text-gray-800">{title}</span>
        <span className="block text-xs text-gray-500">{desc}</span>
      </span>
    </label>
  );
}

export default function SendSmsPage() {
  const toast = useToast();
  const [sourceAddr, setSourceAddr] = useState("");
  const [destination, setDestination] = useState("");
  const [message, setMessage] = useState("");
  const [flash, setFlash] = useState(false);
  const [waitForStatus, setWaitForStatus] = useState(false);

  // Prefill the destination when arriving from an Inbox "Reply" link
  // (/sms/send?to=<number>). Read from the URL directly to avoid a Suspense
  // boundary requirement on useSearchParams.
  useEffect(() => {
    const to = new URLSearchParams(window.location.search).get("to");
    if (to) setDestination(to);
  }, []);
  const [error, setError] = useState("");
  const [result, setResult] = useState<SendResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [touched, setTouched] = useState(false);

  const est = useMemo(() => estimateSegments(message), [message]);
  const { data: ratesData } = useFetch<{ rates: SmsRate[] }>("sms/rates");
  const { data: accountData } = useFetch<Account>("account");
  const lookup = useMemo(() => (ratesData ? buildRateLookup(ratesData.rates) : null), [ratesData]);

  const digits = destination.replace(/\D/g, "");
  const destError = touched && destination && (digits.length < 9 || digits.length > 15) ? "Enter a valid MSISDN (digits only)." : "";

  const cat = lookup && digits.length >= 4 ? categorize(lookup, digits) : null;
  const estCredits = cat ? est.segments * cat.creditsPerSms : est.segments;
  // International requires the sending account to be permitted.
  const blockedIntl = cat?.category === "INTERNATIONAL" && accountData?.allowInternational === false;

  const canSubmit = Boolean(sourceAddr) && digits.length >= 9 && message.trim().length > 0 && !blockedIntl;

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setTouched(true);
    setError("");
    setResult(null);
    if (!canSubmit) return;
    setConfirmOpen(true);
  }

  async function doSend() {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const res = await api<SendResult>("sms/send", {
        method: "POST",
        body: {
          sourceAddr,
          destination: destination.trim(),
          message,
          flash,
          ...(waitForStatus ? { waitMs: 8000, pollMs: 1000 } : {})
        }
      });
      setResult(res);
      notifyAccountChanged(); // refresh the sidebar credit
      toast.success(`SMS queued (#${res.smsId}).`);
      setDestination("");
      setMessage("");
      setFlash(false);
      setWaitForStatus(false);
      setTouched(false);
    } catch (err) {
      setError((err as Error).message);
      toast.error((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Send SMS" subtitle="Send a single message to one recipient." />

      <form onSubmit={onSubmit}>
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Compose */}
          <div className="space-y-6 lg:col-span-2">
            <Card title="Compose" actions={<TemplatePicker onPick={setMessage} />}>
              <div className="space-y-4">
                <SenderSelect value={sourceAddr} onChange={setSourceAddr} required />
                <Input
                  label="Destination"
                  value={destination}
                  onChange={(e) => setDestination(e.target.value)}
                  onBlur={() => setTouched(true)}
                  placeholder="2526xxxxxxxx"
                  inputMode="tel"
                  error={destError}
                  required
                />
                <Textarea
                  label="Message"
                  rows={6}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message…"
                  className="max-h-60 overflow-y-auto"
                  required
                />
              </div>
            </Card>

            <Card title="Options">
              <div className="grid gap-3 sm:grid-cols-2">
                <OptionRow checked={flash} onChange={setFlash} title="Flash SMS" desc="Shows on screen, not saved to the handset." />
                <OptionRow checked={waitForStatus} onChange={setWaitForStatus} title="Wait for delivery" desc="Hold up to 8s for a delivery receipt." />
              </div>
            </Card>

            {blockedIntl && (
              <Alert>International SMS is not enabled for your account. Please contact support to enable it.</Alert>
            )}
            {error && <Alert>{error}</Alert>}

            <Button type="submit" size="lg" disabled={loading || !canSubmit}>
              {loading ? "Sending…" : "Review & Send"}
            </Button>
          </div>

          {/* Live preview */}
          <div className="space-y-6">
            <Card title="Preview">
              <div className="rounded-2xl bg-gray-100 p-4">
                <p className="mb-2 text-center text-[11px] font-medium uppercase tracking-wide text-gray-400">
                  {sourceAddr || "Sender ID"}
                </p>
                <div className="max-h-64 max-w-[85%] overflow-y-auto rounded-2xl rounded-tl-sm bg-white px-3.5 py-2.5 text-sm text-gray-800 shadow-sm ring-1 ring-black/5">
                  {message ? <span className="whitespace-pre-wrap break-words">{message}</span> : <span className="text-gray-400">Your message preview…</span>}
                </div>
              </div>

              <dl className="mt-4 space-y-3">
                <Meter label="Encoding" value={<Badge tone="blue">{est.encoding}</Badge>} />
                <Meter label="Characters" value={<span className="font-semibold tabular-nums text-gray-900">{est.chars}</span>} />
                <Meter label="Segments" value={<span className="font-semibold tabular-nums text-gray-900">{est.segments}</span>} />
                <Meter
                  label="Category"
                  value={cat ? <Badge tone={CAT_TONE[cat.category] ?? "blue"} dot>{CAT_LABEL[cat.category] ?? cat.category}</Badge> : <span className="text-gray-400">—</span>}
                />
                <Meter
                  label="Est. credits"
                  value={
                    <span className="font-semibold tabular-nums text-brand-600">
                      {estCredits}
                      {cat && <span className="ml-1 text-xs font-normal text-gray-400">({est.segments}×{cat.creditsPerSms})</span>}
                    </span>
                  }
                />
              </dl>
            </Card>
          </div>
        </div>
      </form>

      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm SMS"
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={doSend}>Confirm &amp; Send</Button>
          </>
        }
      >
        <SenderHeader sender={sourceAddr} />
        <dl>
          <ConfirmRow label="Destination">{destination.trim() || "—"}</ConfirmRow>
          <ConfirmRow label="Type">{flash ? "Flash (class 0)" : "Standard"}</ConfirmRow>
          <ConfirmRow label="Segments">{est.segments} ({est.encoding}, {est.chars} chars)</ConfirmRow>
          <ConfirmRow label="Category & cost">
            {cat ? `${CAT_LABEL[cat.category] ?? cat.category} · ` : ""}{estCredits} credit{estCredits === 1 ? "" : "s"}
          </ConfirmRow>
          <ConfirmRow label="Delivery wait">{waitForStatus ? "Wait up to 8s" : "No"}</ConfirmRow>
        </dl>
        <MessagePreview message={message} />
        <p className="mt-3 text-xs text-gray-500">
          Please confirm the sender header is correct — it appears as the “from” on the recipient’s phone.
        </p>
      </Modal>

      {result && (
        <Card title="Result">
          <dl className="grid grid-cols-2 gap-y-2 text-sm sm:grid-cols-4">
            <ResultItem label="SMS ID" value={String(result.smsId)} />
            <ResultItem label="Status" value={<Badge tone={result.terminal ? "green" : "blue"} dot>{result.status}</Badge>} />
            <ResultItem label="Encoding" value={result.encoding ?? "—"} />
            <ResultItem label="Segments" value={String(result.segmentCount ?? "—")} />
            <ResultItem label="Credits charged" value={String(result.creditsCharged ?? "—")} />
          </dl>
        </Card>
      )}
    </div>
  );
}

function Meter({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function ResultItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="mt-0.5 font-medium text-gray-900">{value}</dd>
    </div>
  );
}
